import {
    Box,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { DialogContext } from "../../contexts/DialogContext";
import MultiSelectMobile from "./MultiSelectMobile";

export type SearchItem = {
    id?: number | string;
    title?: string;
    recommended?: boolean;
};

// updated
export default function MultiSelect(props: {
    label: string;
    values: SearchItem[];
    selectedIds: (number | string)[];
    onValueChange: (values: (number | string)[]) => void;
    hasSearch?: boolean;
}) {
    const allSelectedCode = -999;
    const values = props.values;
    const selectedValues = props.selectedIds as (number | string)[];

    const [searchedItems, setSearchedItems] = useState<SearchItem[]>(
        props.values
    );
    useEffect(() => {
        // Props change and the useState won't call on rerender.
        // It should be checked on props change.
        setSearchedItems(props.values);
    }, [props.values]);
    const handleSearch = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const searchTerm = event.target.value;
        if (searchTerm.length < 1) {
            setSearchedItems(props.values);
        } else {
            const result = props.values.filter((v) =>
                v.title.includes(searchTerm)
            );
            setSearchedItems(result);
        }
    };

    const isAllSelected = props.selectedIds.length === props.values.length;

    const [open, setOpen] = useState(false);

    const { openDialog } = useContext(DialogContext);

    const theme = useTheme();

    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

    const handleOpen = () => {
        if (!isLargeScreen) {
            openMultiSelectDialog();
        } else {
            setOpen(true);
        }
    };

    const openMultiSelectDialog = () => {
        openDialog(
            <MultiSelectMobile
                label={props.label}
                selectedIds={props.selectedIds}
                values={props.values}
                onValueChange={props.onValueChange}
                hasSearch={props.hasSearch}
            />,
            undefined,
            true,
            {
                zIndex: 1600,
            }
        );
    };

    const handleClose = () => {
        setOpen(false);
    };

    const onSelectedValuesChange = (
        event: SelectChangeEvent<(number | string)[]>
    ) => {
        const {
            target: { value },
        } = event;
        const values = value as (string | number)[];

        if (values.includes(allSelectedCode)) {
            if (props.selectedIds.length === props.values.length) {
                props.onValueChange([]);
            } else {
                props.onValueChange(props.values.map((i) => i.id!));
            }
        } else {
            props.onValueChange(values);
        }
    };

    const renderValues = (selectedIds: (number | string)[]) => {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    m: -0.25,
                }}
            >
                {selectedIds.map((value) => {
                    const valueItem = values.find((c) => c.id === value);

                    if (!valueItem) {
                        return <></>;
                    }

                    return (
                        <Chip
                            key={value}
                            size="small"
                            sx={{ m: 0.25 }}
                            label={valueItem.title}
                        />
                    );
                })}
            </Box>
        );
    };

    return (
        <FormControl sx={{ width: "100%" }}>
            <InputLabel>{props.label}</InputLabel>

            <Select
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                label={props.label}
                value={selectedValues}
                multiple
                onChange={onSelectedValuesChange}
                renderValue={renderValues}
            >
                {props.hasSearch && (
                    <TextField
                        onChange={(e) => handleSearch(e)}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{ width: "100%", "& fieldset": { border: "none" } }}
                        placeholder="جستجو..."
                    />
                )}
                {/*The dividers are separated just to not get mui error from mui select component*/}
                {props.hasSearch && <Divider />}

                {searchedItems.length > 0 && !props.hasSearch && (
                    <MenuItem
                        value={allSelectedCode}
                        sx={{
                            marginTop: "8px",
                        }}
                        disableRipple={true}
                    >
                        <Checkbox checked={isAllSelected} />
                        <ListItemText primary="انتخاب همه" />
                    </MenuItem>
                )}
                {searchedItems.length > 0 && !props.hasSearch && <Divider />}

                {searchedItems.length > 0 ? (
                    searchedItems.map((i) => {
                        return (
                            <MenuItem
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                                key={i.id?.toString()}
                                value={i.id}
                            >
                                <Checkbox
                                    checked={props.selectedIds.includes(i.id!)}
                                />
                                <ListItemText primary={i.title} />
                                {i.recommended && (
                                    <Chip
                                        size="small"
                                        label="پیشنهاد پرشین سازه"
                                        color="secondary"
                                    />
                                )}
                            </MenuItem>
                        );
                    })
                ) : (
                    <Typography sx={{ textAlign: "center", marginY: "20px" }}>
                        موردی یافت نشد.
                    </Typography>
                )}
            </Select>
        </FormControl>
    );
}
